// Libraries
import _ from 'lodash';
import React from 'react';

// App
import {Icon, IconSource, ScrollView, Space, Styled} from '@supermove/components';
import {useHover, useMemo, useResponsive, useState} from '@supermove/hooks';
import {colors} from '@supermove/styles';
import {NativeScrollEvent, NativeSyntheticEvent} from '@supermove/styles/types';

// Components
import Line from '@shared/design/components/Line';
import TextTooltip from '@shared/design/components/TextTooltip';

export const RAIL_WIDTH = 56;
const COLOR = colors.white;
const MAX_OPACITY = 0.95;
const DISPLAY_ICON_COUNT = 5;
const ICON_SPACING = 16;

const getIconButtonWidth = () => {
  // There is a full space between each icon and a half space on each edge
  const spacesWidth = ICON_SPACING * DISPLAY_ICON_COUNT;
  const spaceForIcons = window.innerWidth - spacesWidth;
  return spaceForIcons / DISPLAY_ICON_COUNT;
};

const getEdgeColor = ({iconWidth, offset}: any) => {
  if (offset > iconWidth) {
    return colors.alpha(COLOR, MAX_OPACITY);
  }

  const opacity = offset / iconWidth;
  return colors.alpha(COLOR, Math.min(opacity, MAX_OPACITY));
};

const getOpacityGradient = ({
  scrollEvent,
}: {
  scrollEvent?: NativeSyntheticEvent<NativeScrollEvent>;
}) => {
  if (!scrollEvent) {
    // If there is no scrollEvent, we are at the front without any scrolling yet
    return `linear-gradient(90deg, transparent 0%, transparent 80%, ${colors.alpha(
      COLOR,
      MAX_OPACITY,
    )} 100%)`;
  }

  const position = scrollEvent.nativeEvent.contentOffset.x;
  const toolbarWidth = scrollEvent.nativeEvent.layoutMeasurement.width;
  const contentWidth = scrollEvent.nativeEvent.contentSize.width;
  const iconWidth = getIconButtonWidth();
  const frontColor = getEdgeColor({iconWidth, offset: position});
  const backColor = getEdgeColor({iconWidth, offset: contentWidth - (position + toolbarWidth)});
  return `linear-gradient(90deg, ${frontColor} 0%, transparent 25%, transparent 75%, ${backColor} 100%)`;
};

const RailContainer = Styled.View<{isFullHeight?: boolean}>`
  width: ${RAIL_WIDTH}px;
  padding-top: ${({isFullHeight}) => (isFullHeight ? 16 : 8)}px;
  background-color: ${colors.white};
`;

const RailActionButton = Styled.ButtonV2<{color: string}>`
  height: 40px;
  justify-content: center;
  align-items: center;
  background-color: ${({color}) => color};
`;

const RailActionIconContainer = Styled.View`
  flex: 1;
  align-items: center;
  justify-content: center;
`;

const ToolbarContainer = Styled.View`
  flex-direction: row;
  height: 56px;
  justify-content: space-evenly;
  align-items: center;
  background-color: ${colors.white};
`;

const ToolbarActionButton = Styled.ButtonV2<{isSelected: boolean; width?: number}>`
  height: 40px;
  width: ${({width}) => width || 80}px;
  border-radius: 4px;
  justify-content: center;
  align-items: center;
  background-color: ${({isSelected}) => (isSelected ? colors.blue.accent : colors.white)};
`;

const FadeOverlay = Styled.View<{backgroundImage?: string}>`
  position: absolute;
  width: 100%;
  height: 100%;
  background-image: ${({backgroundImage}) => backgroundImage};
`;

export type RailAction = {
  icon: IconSource;
  handleAction: () => void;
  isSelected: boolean;
  isDisabled?: boolean;
  tooltip?: string;
};
export type RailOptions = {
  actions: RailAction[];
  isLoading?: boolean;
  handleClose?: () => void;
  mobileDrawer?: {
    isOpen: boolean;
    handleClose?: () => void;
    isToolbarVisible?: boolean;
  };
};
export type WithRail = {
  railOptions: RailOptions;
};
export type WithoutRail = {
  railOptions?: never;
};

const RailAction = ({icon, handleAction, tooltip, isSelected, isDisabled = false}: RailAction) => {
  const {ref, isHovered} = useHover();
  const backgroundColor = isSelected || isHovered ? colors.blue.accent : 'transparent';
  const iconColor = !isSelected && isHovered ? colors.blue.hover : colors.blue.interactive;

  return (
    <React.Fragment>
      <Space height={8} />
      <TextTooltip text={tooltip} placement={'left'}>
        <RailActionButton
          ref={ref}
          onPress={handleAction}
          disabled={isDisabled}
          color={backgroundColor}
        >
          <Line
            isVertical
            weight={2}
            color={isSelected || isHovered ? iconColor : 'transparent'}
            style={{position: 'absolute', left: 0}}
          />
          <RailActionIconContainer>
            <Icon source={icon} color={iconColor} size={16} />
          </RailActionIconContainer>
        </RailActionButton>
      </TextTooltip>
      <Space height={8} />
    </React.Fragment>
  );
};

const ToolbarAction = ({
  icon,
  handleAction,
  width,
  isSelected,
  isDisabled = false,
}: RailAction & {width?: number}) => {
  return (
    <ToolbarActionButton
      onPress={handleAction}
      isSelected={isSelected}
      disabled={isDisabled}
      width={width}
    >
      <Icon source={icon} color={colors.blue.interactive} size={20} />
    </ToolbarActionButton>
  );
};

const PaneRail = ({
  railOptions,
  isFullHeight,
}: {
  railOptions: RailOptions;
  isFullHeight?: boolean;
}) => {
  const responsive = useResponsive();
  const [scrollEvent, setScrollEvent] = useState<NativeSyntheticEvent<NativeScrollEvent>>();
  const fadeOverlayBackgroundImage = useMemo(
    () => getOpacityGradient({scrollEvent}),
    [scrollEvent],
  );

  // Desktop vertical rail
  if (responsive.desktop) {
    return (
      <RailContainer isFullHeight={isFullHeight}>
        {railOptions.actions.map((action) => {
          return <RailAction key={_.toString(action.icon)} {...action} />;
        })}
      </RailContainer>
    );
  }

  // Mobile horizontal scrolling toolbar
  if (railOptions.actions.length > DISPLAY_ICON_COUNT) {
    return (
      <ToolbarContainer>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{alignItems: 'center'}}
          onScroll={setScrollEvent}
        >
          <Space width={16} />
          {railOptions.actions.map((action, index) => {
            return (
              <React.Fragment key={_.toString(action.icon)}>
                {index > 0 && <Space width={16} />}
                <ToolbarAction {...action} width={getIconButtonWidth()} />
              </React.Fragment>
            );
          })}
          <Space width={16} />
        </ScrollView>
        <FadeOverlay pointerEvents={'none'} backgroundImage={fadeOverlayBackgroundImage} />
      </ToolbarContainer>
    );
  }

  // Mobile horizontal fixed toolbar
  return (
    <ToolbarContainer>
      {railOptions.actions.map((action) => {
        return <ToolbarAction key={_.toString(action.icon)} {...action} />;
      })}
    </ToolbarContainer>
  );
};

export default PaneRail;
